import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, Copy, Link2, Lock, Users, CheckCircle2, Bell, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

type ShareAudience = 'organization' | 'existing_access' | 'specific_people' | 'tenant_admins';
type ShareCandidate = { id: string; name?: string; email?: string };

type DashboardShareSheetProps = {
    open: boolean;
    dashboardId?: string;
    dashboardTitle: string;
    isPublished: boolean;
    /** True if the current user can share with specific people or use existing_access */
    canShareWithAll: boolean;
    /** True only for admins (r1–r4) who can share with the entire organisation */
    canShareWithOrg: boolean;
    sharedWith: string[];
    publishShareMode: 'me' | 'all';
    availableUsers: ShareCandidate[];
    onClose: () => void;
    onApplySettings: (payload: {
        audience: ShareAudience;
        subjectIds: string[];
        expiresAt: string | null;
    }) => Promise<string>;
    onCopyLink?: (link: string) => void;
};

const EXPIRY_OPTIONS = [
    { id: 'none', label: 'No limit' },
    { id: '1d', label: '1 day' },
    { id: '7d', label: '7 days' },
    { id: '30d', label: '30 days' },
    { id: 'custom', label: 'Custom date/time' },
] as const;

function getAudienceFromShared(
    sharedWith: string[],
    publishShareMode: 'me' | 'all',
    canShareWithOrg: boolean
): ShareAudience {
    if (sharedWith?.includes('*')) return canShareWithOrg ? 'organization' : 'existing_access';
    if (sharedWith && sharedWith.length > 0) return 'specific_people';
    return publishShareMode === 'all' && canShareWithOrg ? 'organization' : 'existing_access';
}

export function DashboardShareSheet({
    open,
    dashboardId,
    dashboardTitle,
    isPublished,
    canShareWithAll,
    canShareWithOrg,
    sharedWith,
    publishShareMode,
    availableUsers,
    onClose,
    onApplySettings,
    onCopyLink,
}: DashboardShareSheetProps) {
    const [audience, setAudience] = useState<ShareAudience>('existing_access');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [emailError, setEmailError] = useState('');
    const [specificPeople, setSpecificPeople] = useState<string[]>([]);
    const [expiryMode, setExpiryMode] = useState<(typeof EXPIRY_OPTIONS)[number]['id']>('none');
    const [customExpiry, setCustomExpiry] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [isLinkReady, setIsLinkReady] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [wasOpen, setWasOpen] = useState(false);

    // Confirmation dialog state
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        if (!open) {
            if (wasOpen) setWasOpen(false);
            return;
        }
        if (wasOpen) return;
        setWasOpen(true);
        const inferredAudience = getAudienceFromShared(sharedWith || [], publishShareMode, canShareWithOrg);
        setAudience(inferredAudience);
        setSpecificPeople((sharedWith || []).filter(v => v && v !== '*'));
        setSelectedUserId('');
        setEmailInput('');
        setEmailError('');
        setExpiryMode('none');
        setCustomExpiry('');
        setGeneratedLink('');
        setIsLinkReady(false);
        setShowConfirm(false);
    }, [open, wasOpen, dashboardId, sharedWith, publishShareMode, canShareWithOrg]);

    const expiresAt = useMemo(() => {
        if (expiryMode === 'none') return null;
        const now = new Date();
        if (expiryMode === '1d') return new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
        if (expiryMode === '7d') return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
        if (expiryMode === '30d') return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
        if (expiryMode === 'custom' && customExpiry) {
            const parsed = new Date(customExpiry);
            if (Number.isNaN(parsed.getTime())) return null;
            return parsed.toISOString();
        }
        return null;
    }, [expiryMode, customExpiry]);

    // ── Add person by dropdown select ─────────────────────────────────────────
    const addBySelect = () => {
        const value = selectedUserId.trim();
        if (!value) return;
        if (!specificPeople.includes(value)) {
            setSpecificPeople(prev => [...prev, value]);
        }
        setSelectedUserId('');
    };

    // ── Add person by email input with org validation ─────────────────────────
    const addByEmail = () => {
        const email = emailInput.trim().toLowerCase();
        if (!email) return;

        const match = availableUsers.find(u => (u.email || '').toLowerCase() === email);
        if (!match) {
            setEmailError('This email does not belong to your organisation.');
            return;
        }
        if (specificPeople.includes(String(match.id))) {
            setEmailInput('');
            setEmailError('');
            return;
        }
        setSpecificPeople(prev => [...prev, String(match.id)]);
        setEmailInput('');
        setEmailError('');
    };

    const removeSpecificPerson = (value: string) => {
        setSpecificPeople(prev => prev.filter(v => v !== value));
    };

    // ── Recipient count for confirmation dialog ───────────────────────────────
    const recipientCount = useMemo(() => {
        if (audience === 'specific_people') return specificPeople.length;
        if (audience === 'organization') return null; // "all users"
        return 0;
    }, [audience, specificPeople]);

    // ── Apply (called after confirmation) ─────────────────────────────────────
    const doApply = async () => {
        if (!dashboardId) return;
        setShowConfirm(false);
        setIsApplying(true);
        try {
            const link = await onApplySettings({
                audience,
                subjectIds: audience === 'specific_people' ? specificPeople : [],
                expiresAt,
            });

            // Dispatch event to refresh sidebar in real-time
            window.dispatchEvent(new CustomEvent('refresh-my-content'));

            if (link && link.includes('st=') && link.includes('kv=')) {
                setGeneratedLink(link);
                setIsLinkReady(true);
            } else {
                setGeneratedLink('');
                setIsLinkReady(false);
            }
        } finally {
            setIsApplying(false);
        }
    };

    // ── Click Apply → show confirmation first ─────────────────────────────────
    const handleApplyClick = () => {
        if (audience === 'specific_people' && specificPeople.length > 0) {
            setShowConfirm(true);
        } else {
            doApply();
        }
    };

    const handleCopy = async () => {
        if (!generatedLink) return;
        
        try {
            if (onCopyLink) {
                onCopyLink(generatedLink);
            } else {
                await navigator.clipboard.writeText(generatedLink);
            }
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const canApply = isPublished && !isApplying && !!dashboardId && !(audience === 'specific_people' && specificPeople.length === 0);

    return (
        <>
            {/* ── Main share sheet dialog ──────────────────────────────────────── */}
            <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
                <DialogContent className="sm:max-w-[680px] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Link settings</DialogTitle>
                        <DialogDescription>{dashboardTitle || 'Dashboard'}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5">
                        {!isPublished && (
                            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2.5">
                                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                                <p className="text-xs text-amber-800 dark:text-amber-300">
                                    This dashboard is not published yet. <span className="font-semibold">Publish it first</span> before generating a share link.
                                </p>
                            </div>
                        )}

                        <div className={`space-y-2 ${!isPublished ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                            <p className="text-sm font-semibold">The link works for</p>
                            <RadioGroup
                                value={audience}
                                onValueChange={(v) => setAudience(v as ShareAudience)}
                                className="space-y-2"
                            >
                                {/* Organization-wide — admins only */}
                                {canShareWithOrg && (
                                    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${audience === 'organization' ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                                        <Building2 className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">Everyone in this tenant</p>
                                            <p className="text-xs text-slate-500">All active users in your tenant can open this link.</p>
                                        </div>
                                        <RadioGroupItem value="organization" />
                                    </label>
                                )}

                                {/* Existing access — all sharing-capable roles */}
                                {canShareWithAll && (
                                    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${audience === 'existing_access' ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                                        <Lock className="h-4 w-4 mt-0.5 text-slate-600 dark:text-slate-300" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">Only people with existing access</p>
                                            <p className="text-xs text-slate-500">Creator and tenant admins retain access.</p>
                                        </div>
                                        <RadioGroupItem value="existing_access" />
                                    </label>
                                )}

                                {/* Specific people — all sharing-capable roles */}
                                {canShareWithAll && (
                                    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${audience === 'specific_people' ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                                        <Users className="h-4 w-4 mt-0.5 text-indigo-600 dark:text-indigo-400" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">People you choose</p>
                                            <p className="text-xs text-slate-500">
                                                Allow selected users only (same tenant).
                                                {!canShareWithOrg && (
                                                    <span className="ml-1 text-amber-600 dark:text-amber-400">
                                                        You cannot share with the entire tenant.
                                                    </span>
                                                )}
                                            </p>

                                            {audience === 'specific_people' && (
                                                <div className="mt-3 space-y-2" onClick={e => e.preventDefault()}>
                                                    {/* Dropdown picker */}
                                                    <div className="flex gap-2">
                                                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                                            <SelectTrigger className="flex-1">
                                                                <SelectValue placeholder="Select user…" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {availableUsers
                                                                    .filter(u => !specificPeople.includes(String(u.id)))
                                                                    .map((u) => (
                                                                        <SelectItem key={u.id} value={String(u.id)}>
                                                                            <div className="flex flex-col">
                                                                                <span>{u.name || u.email || u.id}</span>
                                                                                {u.name && u.email && (
                                                                                    <span className="text-xs text-slate-500">{u.email}</span>
                                                                                )}
                                                                            </div>
                                                                        </SelectItem>
                                                                    ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <button
                                                            type="button"
                                                            onClick={addBySelect}
                                                            disabled={!selectedUserId}
                                                            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-40"
                                                        >
                                                            Add
                                                        </button>
                                                    </div>

                                                    {/* Email input with org validation */}
                                                    <div className="flex gap-2">
                                                        <Input
                                                            type="email"
                                                            placeholder="Or type email address…"
                                                            value={emailInput}
                                                            onChange={(e) => {
                                                                setEmailInput(e.target.value);
                                                                setEmailError('');
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') { e.preventDefault(); addByEmail(); }
                                                            }}
                                                            className={`flex-1 text-sm ${emailError ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={addByEmail}
                                                            disabled={!emailInput.trim()}
                                                            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-40"
                                                        >
                                                            Add
                                                        </button>
                                                    </div>
                                                    {emailError && (
                                                        <p className="text-xs text-red-500 flex items-center gap-1">
                                                            <AlertTriangle className="h-3 w-3" />
                                                            {emailError}
                                                        </p>
                                                    )}

                                                    {/* Selected people chips */}
                                                    {specificPeople.length > 0 && (
                                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                                            {specificPeople.map((item) => {
                                                                const user = availableUsers.find(u => String(u.id) === item);
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        key={item}
                                                                        onClick={() => removeSpecificPerson(item)}
                                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-red-50 hover:border-red-300 dark:hover:bg-red-900/20 transition-colors"
                                                                    >
                                                                        {user ? (user.name ? `${user.name} (${user.email})` : user.email || user.id) : item}
                                                                        <span className="text-[10px] opacity-60 ml-0.5">×</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <RadioGroupItem value="specific_people" />
                                    </label>
                                )}

                                {/* Fallback for non-sharing users */}
                                {!canShareWithAll && (
                                    <label className={`flex items-start gap-3 rounded-lg border p-3 ${audience === 'tenant_admins' ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                                        <Users className="h-4 w-4 mt-0.5 text-indigo-600 dark:text-indigo-400" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">Publish to tenant admins</p>
                                            <p className="text-xs text-slate-500">Only active tenant admins can access via this link.</p>
                                        </div>
                                        <RadioGroupItem value="tenant_admins" />
                                    </label>
                                )}
                            </RadioGroup>
                        </div>

                        <div className={`space-y-2 ${!isPublished ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                            <p className="text-sm font-semibold">More settings</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Permission</p>
                                    <Select value="view-only" onValueChange={() => {}}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="view-only">Can view</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Expiration</p>
                                    <Select value={expiryMode} onValueChange={(v) => setExpiryMode(v as any)}>
                                        <SelectTrigger><SelectValue placeholder="Select expiration" /></SelectTrigger>
                                        <SelectContent>
                                            {EXPIRY_OPTIONS.map(opt => (
                                                <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {expiryMode === 'custom' && (
                                        <Input
                                            type="datetime-local"
                                            value={customExpiry}
                                            onChange={(e) => setCustomExpiry(e.target.value)}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={`space-y-2 ${!isPublished ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                            <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Access Link</label>
                            <div className="w-full min-w-0 max-w-full overflow-hidden">
                                <Input
                                    readOnly
                                    value={isLinkReady ? generatedLink : 'Click Apply to generate a secure encrypted share link'}
                                    title={generatedLink || 'Click Apply to generate a secure encrypted share link'}
                                    className="w-full min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap bg-slate-50 dark:bg-slate-800"
                                />
                            </div>
                            <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                <Link2 className="h-3.5 w-3.5" />
                                Link includes encrypted token with dashboard, audience/recipients, and expiry validation.
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <p className="mr-auto text-[11px] text-slate-500">
                            Applying new audience/recipients revokes the previous shared link.
                        </p>
                        <button type="button" onClick={onClose} className="px-3.5 py-2 rounded-md text-sm border border-slate-300 dark:border-slate-700">
                            Close
                        </button>
                        <button
                            type="button"
                            onClick={handleApplyClick}
                            disabled={!canApply}
                            title={!isPublished ? 'Publish the dashboard first to generate a share link' : undefined}
                            className="px-3.5 py-2 rounded-md text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isApplying ? 'Applying…' : 'Apply'}
                        </button>
                        <button
                            type="button"
                            onClick={handleCopy}
                            disabled={!isLinkReady || !generatedLink}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 transition-all active:scale-95"
                        >
                            {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            {isCopied ? 'Copied!' : 'Copy Link'}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Alert confirmation dialog ─────────────────────────────────────── */}
            <Dialog open={showConfirm} onOpenChange={(v) => !v && setShowConfirm(false)}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bell className="h-4 w-4 text-blue-500" />
                            Send sharing notification?
                        </DialogTitle>
                        <DialogDescription>
                            An in-app notification will be sent to{' '}
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                                {recipientCount} {recipientCount === 1 ? 'person' : 'people'}
                            </span>{' '}
                            letting them know that{' '}
                            <span className="font-semibold text-slate-800 dark:text-slate-200">"{dashboardTitle}"</span>{' '}
                            has been shared with them.
                        </DialogDescription>
                    </DialogHeader>

                    {specificPeople.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 py-1">
                            {specificPeople.map(id => {
                                const u = availableUsers.find(x => String(x.id) === id);
                                return (
                                    <span
                                        key={id}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300"
                                    >
                                        <CheckCircle2 className="h-3 w-3" />
                                        {u?.name || u?.email || id}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        They will see the notification in their alert centre and can access the dashboard from the Published Dashboards section in the sidebar.
                    </p>

                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => setShowConfirm(false)}
                            className="px-3.5 py-2 rounded-md text-sm border border-slate-300 dark:border-slate-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={doApply}
                            className="px-3.5 py-2 rounded-md text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                            Confirm & Share
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
